import React, { useState, useEffect, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { Box, Alert, useMediaQuery, useTheme } from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import Navbar from './Navbar';
import DynamicSidebar from '../DynamicSidebar';
import { ContentLoader } from '../PageLoader';
import { useBadges } from '../../utils/badgeStore';

const drawerWidth = 260;

// ── Subscription warning banner shown at the top of every admin page ──────────
const SubscriptionBanner = () => {
  const [dismissed, setDismissed] = useState(false);
  // Reuse the notifications already fetched by the shared poller — no extra request.
  const { notifications } = useBadges();

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const eligible = Boolean(user?.gymId) && user?.role !== 'member';
  const banner = eligible
    ? notifications.find(n =>
        !n.isRead &&
        (n.type === 'subscription_warning' || n.type === 'subscription_expired')
      )
    : null;

  if (dismissed || !banner) return null;

  const isExpired = banner.type === 'subscription_expired';

  return (
    <Alert
      severity={isExpired ? 'error' : 'warning'}
      icon={<WarningIcon />}
      onClose={() => setDismissed(true)}
      sx={{
        borderRadius: 0,
        py: 0.75,
        fontWeight: 600,
        fontSize: 13,
        bgcolor: isExpired ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.12)',
        border: 'none',
        borderBottom: `2px solid ${isExpired ? '#ef4444' : '#f59e0b'}`,
        color: isExpired ? '#fca5a5' : '#fde68a',
        '& .MuiAlert-icon': { color: isExpired ? '#f87171' : '#fbbf24' },
        '& .MuiAlert-action .MuiButtonBase-root': { color: isExpired ? '#fca5a5' : '#fde68a' }
      }}
    >
      {banner.title}: {banner.message}
    </Alert>
  );
};

const Layout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(!isMobile);

  useEffect(() => {
    setOpen(!isMobile);
  }, [isMobile]);

  const toggleDrawer = () => setOpen(!open);

  return (
    <Box sx={{ display: 'flex', width: '100%', flexDirection: 'column', minHeight: '100vh' }}>
      <Navbar open={open} toggleDrawer={toggleDrawer} />
      <SubscriptionBanner />
      <Box sx={{ display: 'flex', flex: 1 }}>
        <DynamicSidebar open={open} onClose={toggleDrawer} isMobile={isMobile} drawerWidth={drawerWidth} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 1, sm: 2 },
            pt: { xs: 9, sm: 10 },
            minHeight: 'calc(100vh - 48px)',
            bgcolor: 'background.default',
            minWidth: 0,
            transition: theme.transitions.create(['margin', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          {/* Lazy pages suspend here, not at the App-level boundary,
              so the Navbar/Sidebar shell never unmounts on navigation */}
          <Suspense fallback={<ContentLoader />}>
            <Outlet />
          </Suspense>
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
